import { type ComponentProps, useEffect, useMemo, useState } from "react"
import { format } from "date-fns"
import { LineChart, ReferenceLine } from "recharts"
import cn from "@/v2-ui/utils/utils.cn"
import Chart from "@/v2-ui/chart"
import {
  UPTIME_RANGE_OPTIONS,
  type UptimeRangeValue,
} from "@/v2-backoffice-client/uptime/uptime.constants"
import { useUptimeViewResponseTimesQuery } from "@/v2-backoffice-client/uptime/view/responseTimes/uptime.view.responseTimes.query"
import type { UptimeViewResponseTimesQueryVariables } from "@/v2-backoffice-client/uptime/view/responseTimes/__types__/uptime.view.responseTimes.query"

import UptimeLiveIndicator from "@/v2-backoffice-client/uptime/view/UptimeLiveIndicator"
import type { UptimeLiveProbeState } from "@/v2-backoffice-client/uptime/view/runProbe/useUptimeLiveProbe"

export type UptimeViewResponseChartProps = ComponentProps<"div"> & {
  source: string
  identifier: string
  // Optional 90d baseline (p95) drawn as a horizontal reference line
  // so the reader can see at a glance whether the current range is
  // faster, around, or slower than normal.
  baselineP95Ms?: number | null
  // Optional 90d median drawn as a second reference line.
  baselineP50Ms?: number | null
  // When provided, a small "Live" pill is rendered next to the range
  // switcher showing the current auto-probing state.
  liveState?: UptimeLiveProbeState
  // Refetch the chart's own data; the parent wires this into the
  // live-probe hook's onProbe callback so the chart reflects every
  // new sample.
  refetchRef?: React.MutableRefObject<(() => void) | null>
}

function formatBucketLabel(iso: string, range: UptimeRangeValue): string {
  const d = new Date(iso)
  if (range === "HOUR" || range === "DAY") {
    return format(d, "HH:mm")
  }
  return format(d, "d. MMM, HH")
}

const CHART_CONFIG = {
  avg: {
    label: "Avg response",
    color: "hsl(var(--primary))",
  },
  max: {
    label: "Max response",
    color: "hsl(var(--warning))",
  },
  min: {
    label: "Min response",
    color: "hsl(var(--success-1))",
  },
}

function UptimeViewResponseChart(props: UptimeViewResponseChartProps) {
  const {
    source,
    identifier,
    baselineP95Ms,
    baselineP50Ms,
    liveState,
    refetchRef,
    className,
    ...divProps
  } = props

  const [range, setRange] = useState<UptimeRangeValue>("DAY")

  // Cast the string-literal union to the generated TS enum; values
  // are identical at runtime (both are the enum's string values) but
  // tsc needs the nominal enum type.
  const rangeVar =
    range as unknown as UptimeViewResponseTimesQueryVariables["range"]

  const { data, loading, error, refetch } = useUptimeViewResponseTimesQuery({
    variables: {
      input: { source, identifier },
      range: rangeVar,
    },
    fetchPolicy: "cache-and-network",
  })

  // Expose the active refetch to the parent so the live-probe hook
  // can pull fresh chart data after every mutation without having
  // to re-create the query here. We install the handle in an effect
  // (instead of mutating the ref during render) so React isn't
  // surprised by side effects in the render path -- and so the
  // ref always points at the *current* render's `refetch`, not a
  // stale closure from a previous render.
  useEffect(() => {
    if (!refetchRef) return
    refetchRef.current = () => {
      refetch()
    }
    return () => {
      // Drop the handle on unmount so the parent never invokes a
      // refetch on a query whose hook has gone away.
      refetchRef.current = null
    }
  }, [refetchRef, refetch])

  const chartData = useMemo(() => {
    const points = data?.getUptimeResponseTimes ?? []
    return points.map((p) => ({
      label: formatBucketLabel(String(p.BucketStart), range),
      avg: p.AvgDurationMs,
      min: p.MinDurationMs,
      max: p.MaxDurationMs,
      down: p.DownCount,
    }))
  }, [data, range])

  const hasData = chartData.some((p) => p.avg != null)

  return (
    <div
      className={cn("uptime-view-chart-frame flex flex-col gap-4", className)}
      {...divProps}
    >
      <div className="uptime-view-chart-header">
        <div className="font-medium flex items-center gap-2">
          Response times
          {liveState && <UptimeLiveIndicator state={liveState} />}
        </div>
        <div className="inline-flex items-center rounded-md border border-border overflow-hidden text-xs">
          {UPTIME_RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={cn(
                "px-3 py-1.5 transition-colors",
                range === opt.value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted",
              )}
              onClick={() => setRange(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="text-sm text-destructive">
          Failed to load response times: {error.message}
        </div>
      )}

      {!hasData && !loading && !error && (
        <div className="text-sm text-muted-foreground text-center py-10">
          No response-time data in this range yet.
        </div>
      )}

      {hasData && (
        <Chart config={CHART_CONFIG} className="w-full h-[260px]">
          <LineChart data={chartData} margin={{ left: -10, right: 10 }}>
            <Chart.Grid vertical={false} />
            <Chart.Axis.X
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={30}
              className="text-xs text-muted-foreground"
            />
            <Chart.Axis.Y
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={54}
              tickFormatter={(v) => `${v}ms`}
              className="text-xs text-muted-foreground"
            />
            <Chart.Tooltip
              content={<Chart.TooltipContent indicator="line" />}
            />
            <Chart.Legend content={<Chart.LegendContent />} />
            {/* 90d baseline reference lines so the reader can judge
                "how does right now compare to normal?" at a glance. */}
            {baselineP50Ms != null && baselineP50Ms > 0 && (
              <ReferenceLine
                y={baselineP50Ms}
                stroke="hsl(var(--muted-foreground))"
                strokeOpacity={0.55}
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
                label={{
                  value: `p50 baseline ${baselineP50Ms}ms`,
                  position: "insideTopRight",
                  fill: "hsl(var(--muted-foreground))",
                  fontSize: 10,
                }}
              />
            )}
            {baselineP95Ms != null && baselineP95Ms > 0 && (
              <ReferenceLine
                y={baselineP95Ms}
                stroke="rgb(217 119 6)"
                strokeOpacity={0.7}
                strokeDasharray="6 3"
                ifOverflow="extendDomain"
                label={{
                  value: `p95 baseline ${baselineP95Ms}ms`,
                  position: "insideBottomRight",
                  fill: "rgb(217 119 6)",
                  fontSize: 10,
                }}
              />
            )}
            <Chart.Line
              type="monotone"
              dataKey="max"
              stroke="var(--color-max)"
              strokeWidth={1.5}
              dot={false}
              connectNulls
            />
            <Chart.Line
              type="monotone"
              dataKey="avg"
              stroke="var(--color-avg)"
              strokeWidth={2.5}
              dot={false}
              connectNulls
            />
            <Chart.Line
              type="monotone"
              dataKey="min"
              stroke="var(--color-min)"
              strokeWidth={1.5}
              strokeDasharray="3 3"
              dot={false}
              connectNulls
            />
          </LineChart>
        </Chart>
      )}
    </div>
  )
}

export default UptimeViewResponseChart
